import React, {
    useContext, useEffect, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import type { UnitType } from '@r6digital/rapidstor-sdk/domain';

import SkeletonLoader from '@/components/SkeletonLoader';
import useRapidStorTracking from '@/core/hooks/useRapidStorTracking';
import { JourneyContext } from '@/features/rapidstor/providers/JourneyProvider';
import store from '@/store';
import { setDetailsModal } from '@/store/bookingSlice';
import classes from '@/standard-unit-listing/scss/index.module.scss';

import QuickSizeFilter from '../components/elements/quick-size-filter';
import UnitTypeCard from '../components/unit-type';
import { siteFor } from '../sdk/context';
import { toLegacyUnitType } from '../sdk/interop';
import { buildListing } from '../sdk/listing';
import type { SizeFilter } from '../sdk/listing';

/**
 * The standard unit listing, built on `@r6digital/rapidstor-sdk`.
 *
 * All of the sorting, grouping, floor and size logic that used to live in this file now sits in
 * `../sdk/listing.ts`, expressed as a pipeline over the SDK's collections. What remains here is the
 * screen's own concerns: loading state, the quick filter, tracking and the modal handoff.
 */
function StandardUnitListingIndex() {
    const {
        descriptorList, location, unitListLoading, unitListSuccess,
    } = useContext(JourneyContext);
    const [sizeFilter, setSizeFilter] = useState<SizeFilter>('all');
    const { trackEvent } = useRapidStorTracking();
    const { t } = useTranslation();

    const listing = useMemo(
        () => (location ? buildListing(descriptorList, location, sizeFilter) : null),
        [descriptorList, location, sizeFilter],
    );

    const showQuickSearch = useMemo(
        () => (location ? siteFor(location).settings.listing().showQuickSearch() : false),
        [location],
    );

    useEffect(() => {
        if (unitListSuccess && !unitListLoading) {
            trackEvent('unit-listing', {});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unitListLoading, unitListSuccess]);

    if (!location || !listing) {
        return null;
    }

    const openModal = (
        type: 'bookNow' | 'showPrices' | 'contactMe',
        event: string,
    ) => (unitType: UnitType) => {
        trackEvent(event, {
            unitType: unitType.typeName,
            unitSize: unitType.formatDimensions({ spaceAroundSeparator: false }),
        });
        store.dispatch(
            setDetailsModal({
                isOpen: true,
                // Interop: the modal is typed against the app's flattened shape. See sdk/interop.ts.
                unitType: toLegacyUnitType(unitType, descriptorList) ?? null,
                type,
            }),
        );
    };

    return (
        <div className={classes.container} id="standard-unit-listing">
            {showQuickSearch ? (
                <QuickSizeFilter
                    value={sizeFilter}
                    onChange={setSizeFilter}
                    location={location}
                />
            ) : null}

            <div
                className={classes.unitTypeGrid}
                data-testid={
                    unitListLoading ? 'unit-type-grid-loading' : 'unit-type-grid-loaded'
                }
            >
                {unitListLoading && <SkeletonLoader />}

                {!unitListLoading
                    && listing.cards.map((card) => (
                        <UnitTypeCard
                            key={card.map((unitType) => unitType.id).join('-')}
                            variants={card}
                            location={location}
                            descriptors={descriptorList}
                            onBookOnline={openModal('bookNow', 'book_online_clicked')}
                            onShowPrices={openModal('showPrices', 'show_prices_clicked')}
                            onSendEnquiry={openModal('contactMe', 'send_enquiry_clicked')}
                        />
                    ))}

                {!unitListLoading && unitListSuccess && listing.isEmpty && (
                    <p className={classes.emptyState} data-testid="unit-listing-empty">
                        {t('standardUnitListing:noUnitsAvailable')}
                    </p>
                )}
            </div>
        </div>
    );
}

export default StandardUnitListingIndex;
